// Pursuit Risk Widget - Shows Heuristic Fit Score, risk level, and red flags
// Displays compliance officer intelligence on the Pursuit Detail page
// Note: pWin scoring is deprecated - using Heuristic Fit Score instead

import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus,
  AlertTriangle,
  Shield,
  RefreshCw,
  Loader2,
  Target,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Zap,
  HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import ScoreSourceBadge from '../ui/ScoreSourceBadge';
import { 
  getEffectiveScore, 
  getScoreSource, 
  getPrerequisiteStatus,
  SCORE_SOURCE,
  SCORE_SOURCE_CONFIG
} from '../../utils/scoreSourceUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Risk level configuration
const RISK_CONFIG = {
  HIGH: { color: 'text-red-600', bg: 'bg-red-100', border: 'border-red-200', label: 'High Risk' },
  MEDIUM: { color: 'text-amber-600', bg: 'bg-amber-100', border: 'border-amber-200', label: 'Medium Risk' },
  LOW: { color: 'text-emerald-600', bg: 'bg-emerald-100', border: 'border-emerald-200', label: 'Low Risk' }
};

// Trend icons
const TREND_ICONS = {
  up: TrendingUp,
  down: TrendingDown,
  stable: Minus
};

export function PursuitRiskWidget({ pursuitId, authToken, pursuit }) {
  const [auditData, setAuditData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Calculate effective score with source detection
  const effectiveScoreData = getEffectiveScore(pursuit);
  const scoreSource = getScoreSource(pursuit);
  
  // Use the unified effective score — already handles baseline as floor
  const heuristicFitScore = effectiveScoreData.score;
  const riskLevel = auditData?.scores?.risk_level ?? pursuit?.risk_level ?? null;
  const scoreTrend = auditData?.scores?.pwin_trend ?? pursuit?.pwin_trend ?? 'stable';
  const redFlags = auditData?.red_flags ?? [];
  const missingMandatory = auditData?.missing_mandatory ?? [];

  // Memoized factor breakdown — same Object.entries + filter + map pipeline
  // ran on every render before. Now only recomputes when the pursuit's
  // breakdown identity changes (after a fresh AI analysis run).
  const fitFactorRows = useMemo(() => {
    const details = pursuit?.fit_score_breakdown?.details;
    if (!details) return [];
    return Object.entries(details)
      .filter(([key, val]) => key.startsWith('factor_') && val?.score !== undefined && val?.max !== undefined)
      .map(([key, factor]) => {
        const pct = Math.round((factor.score / factor.max) * 100);
        const isStrong = pct >= 60;
        const isGap = pct < 40;
        const barColor = isStrong ? 'bg-emerald-500' : isGap ? 'bg-red-400' : 'bg-amber-500';
        const labelColor = isStrong ? 'text-emerald-700' : isGap ? 'text-red-600' : 'text-amber-600';
        return { key, factor, pct, barColor, labelColor };
      });
  }, [pursuit?.fit_score_breakdown?.details]);

  const runComplianceAudit = async () => {
    if (!pursuitId || !authToken) return;
    
    setIsRefreshing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/ai/compliance-audit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ pursuit_id: pursuitId })
      });
      
      if (!response.ok) {
        throw new Error('Failed to run compliance audit');
      }
      
      const data = await response.json();
      setAuditData(data);
      toast.success('Compliance audit complete');
    } catch (err) {
      console.error('Error running compliance audit:', err);
      toast.error('Failed to run compliance audit');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Get risk configuration
  const riskConfig = RISK_CONFIG[riskLevel] || RISK_CONFIG.MEDIUM;
  const TrendIcon = TREND_ICONS[scoreTrend] || Minus;

  // pWin color based on score
  const getPwinColor = (score) => {
    if (score >= 70) return 'text-emerald-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <Card className={`border-2 ${riskConfig.border} shadow-sm`} data-testid="pursuit-risk-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className={`w-8 h-8 ${riskConfig.bg} rounded-lg flex items-center justify-center`}>
              <Shield className={`w-4 h-4 ${riskConfig.color}`} />
            </div>
            AI Risk Assessment
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={runComplianceAudit}
            disabled={isRefreshing}
            data-testid="refresh-audit-btn"
          >
            {isRefreshing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Check if RFP documents are missing */}
        {(!pursuit?.rfp_document_count || pursuit.rfp_document_count === 0) ? (
          <div className="text-center py-6 space-y-3" data-testid="rfp-upload-required-message">
            <div className="w-16 h-16 mx-auto rounded-xl bg-amber-50 border-2 border-dashed border-amber-300 flex items-center justify-center">
              <AlertCircle className="w-7 h-7 text-amber-500" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700">RFP Document Required</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-[220px] mx-auto">
                Upload at least 1 RFP or Solicitation document in the Document Ingestion Hub to generate the Fit Score and Risk Assessment.
              </p>
            </div>
            {(!pursuit?.capability_document_count || pursuit.capability_document_count === 0) && (
              <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-[11px] text-blue-700 flex items-center gap-1.5 justify-center">
                  <Zap className="w-3.5 h-3.5" />
                  Also upload a capability document for Fit assessment
                </p>
              </div>
            )}
          </div>
        ) : (
        <>
        {/* Score Source Badge */}
        <div className="flex justify-end">
          <ScoreSourceBadge source={scoreSource} compact={false} />
        </div>

        {/* Prerequisites Checklist (shown when incomplete) */}
        {scoreSource === SCORE_SOURCE.PREREQUISITES_INCOMPLETE && (
          <div className="space-y-2" data-testid="prerequisites-checklist">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-red-500" />
              <span className="text-sm font-semibold text-slate-700">Prerequisites Incomplete</span>
            </div>
            {(() => {
              const prereqs = getPrerequisiteStatus(pursuit);
              return (
                <div className="space-y-2">
                  <div className={`flex items-center gap-2 p-2 rounded-lg border ${prereqs.metadata.complete ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
                    {prereqs.metadata.complete ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                    <div>
                      <p className={`text-sm font-medium ${prereqs.metadata.complete ? 'text-emerald-700' : 'text-red-700'}`}>Metadata Complete</p>
                      {!prereqs.metadata.complete && prereqs.metadata.missing.length > 0 && (
                        <p className="text-xs text-red-500">Missing: {prereqs.metadata.missing.join(', ')}</p>
                      )}
                    </div>
                  </div>
                  <div className={`flex items-center gap-2 p-2 rounded-lg border ${prereqs.documents.complete ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
                    {prereqs.documents.complete ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                    <div>
                      <p className={`text-sm font-medium ${prereqs.documents.complete ? 'text-emerald-700' : 'text-red-700'}`}>Documents Uploaded</p>
                      {!prereqs.documents.complete && prereqs.documents.missing.length > 0 && (
                        <p className="text-xs text-red-500">Missing: {prereqs.documents.missing.join(', ')}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Analysis Required State */}
        {scoreSource === SCORE_SOURCE.ANALYSIS_REQUIRED && (
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="analysis-required-state">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-700">Analysis Required</span>
            </div>
            <p className="text-xs text-amber-600 mb-2">Prerequisites met. Run AI Analysis on the Ingestion Hub to calculate the Fit Score.</p>
            <div className="text-2xl font-bold text-amber-400" style={{ fontFamily: 'Manrope, sans-serif' }}>--</div>
          </div>
        )}

        {/* AI Analyzed Score Display */}
        {scoreSource === SCORE_SOURCE.AI_ANALYZED && (
        <>
        <div className="grid grid-cols-2 gap-4">
          {/* Heuristic Fit Score */}
          <div className={`p-4 rounded-xl ${riskConfig.bg}`}>
            <div className="flex items-center gap-2 mb-1">
              <Target className={`w-4 h-4 ${riskConfig.color}`} />
              <span className="text-xs text-slate-600 uppercase tracking-wider font-medium">Fit Score</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-sm">
                      Fit Score measures alignment between the RFP requirements and your team's capabilities. Calculated by AI Analysis across 5 factors.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-bold ${heuristicFitScore !== null ? getPwinColor(heuristicFitScore) : 'text-slate-400'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                {heuristicFitScore !== null ? `${heuristicFitScore}` : '--'}
              </span>
              {heuristicFitScore !== null && (
                <div className={`flex items-center ${scoreTrend === 'up' ? 'text-emerald-600' : scoreTrend === 'down' ? 'text-red-600' : 'text-slate-400'}`}>
                  <TrendIcon className="w-4 h-4" />
                </div>
              )}
            </div>
          </div>
          
          {/* Risk Level */}
          <div className={`p-4 rounded-xl ${riskConfig.bg}`}>
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className={`w-4 h-4 ${riskConfig.color}`} />
              <span className="text-xs text-slate-600 uppercase tracking-wider font-medium">Risk Level</span>
            </div>
            <Badge className={`${riskConfig.bg} ${riskConfig.color} border ${riskConfig.border} text-sm px-3 py-1`}>
              {riskLevel || 'Not Assessed'}
            </Badge>
          </div>
        </div>

        {/* Fit Score Factor Breakdown */}
        {pursuit?.fit_score_breakdown?.details && (
          <div className="space-y-3" data-testid="fit-score-breakdown">
            {/* NAICS/PSC Eligibility Warning Banner */}
            {pursuit.fit_score_breakdown.details?.naics_psc_warning?.status && (
              <div className={`flex items-start gap-2 p-3 rounded-lg border ${
                pursuit.fit_score_breakdown.details.naics_psc_warning.status === 'PASS' 
                  ? 'bg-emerald-50 border-emerald-200' 
                  : pursuit.fit_score_breakdown.details.naics_psc_warning.status === 'WARN'
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-red-50 border-red-200'
              }`} data-testid="naics-psc-warning">
                {pursuit.fit_score_breakdown.details.naics_psc_warning.status === 'PASS' ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                    pursuit.fit_score_breakdown.details.naics_psc_warning.status === 'WARN' ? 'text-amber-600' : 'text-red-600'
                  }`} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold ${
                      pursuit.fit_score_breakdown.details.naics_psc_warning.status === 'PASS' ? 'text-emerald-700' 
                      : pursuit.fit_score_breakdown.details.naics_psc_warning.status === 'WARN' ? 'text-amber-700' 
                      : 'text-red-700'
                    }`}>
                      NAICS/PSC Eligibility: {pursuit.fit_score_breakdown.details.naics_psc_warning.status}
                    </span>
                  </div>
                  {pursuit.fit_score_breakdown.details.naics_psc_warning.notes?.map((note, i) => (
                    <p key={i} className="text-[11px] text-slate-600 mt-0.5">{note}</p>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-slate-700">Score Breakdown</span>
            </div>
            
            <div className="space-y-2.5">
              {fitFactorRows.map(({ key, factor, pct, barColor, labelColor }) => (
                  <div key={key} className="space-y-1" data-testid={`breakdown-factor-${key}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-700">{factor.name}</span>
                      <span className={`text-xs font-bold ${labelColor}`}>{factor.score}/{factor.max}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    {/* Show notes — strengths and gaps */}
                    {factor.notes && factor.notes.length > 0 && (
                      <div className="space-y-0.5 pl-1">
                        {factor.notes.map((note, nIdx) => {
                          const isPositive = note.includes('match') || note.includes('found') || note.includes('confirmed') || note.includes('evidence') || note.includes('covers');
                          return (
                            <div key={nIdx} className="flex items-start gap-1.5">
                              {isPositive ? (
                                <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                              ) : (
                                <AlertCircle className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                              )}
                              <span className="text-[11px] text-slate-600 leading-tight">{note}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
              ))}
            </div>
          </div>
        )}
        </>
        )}

        {/* Red Flags Section */}
        {(redFlags.length > 0 || missingMandatory.length > 0) && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-red-500" />
              <span className="text-sm font-semibold text-slate-700">Red Flags & Gaps</span>
            </div>
            
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {/* Red Flags */}
              {redFlags.map((flag, index) => (
                <div 
                  key={`flag-${index}`}
                  className="flex items-start gap-2 p-2 bg-red-50 rounded-lg border border-red-100"
                  data-testid={`red-flag-${index}`}
                >
                  <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-red-700">{flag.flag}</p>
                    {flag.recommendation && (
                      <p className="text-xs text-red-500 mt-0.5">{flag.recommendation}</p>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Missing Mandatory Items */}
              {missingMandatory.slice(0, 5).map((item, index) => (
                <div 
                  key={item.id || item.item || `missing-${index}`}
                  className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg border border-amber-100"
                  data-testid={`missing-item-${index}`}
                >
                  <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-700">{item.item}</p>
                </div>
              ))}
              
              {missingMandatory.length > 5 && (
                <p className="text-xs text-slate-500 text-center py-1">
                  +{missingMandatory.length - 5} more items
                </p>
              )}
            </div>
          </div>
        )}

        {/* No Issues */}
        {heuristicFitScore !== null && redFlags.length === 0 && missingMandatory.length === 0 && (
          <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <span className="text-sm text-emerald-700 font-medium">No critical issues detected</span>
          </div>
        )}

        {/* Run Audit CTA */}
        {heuristicFitScore === null && (
          <Button 
            className="w-full" 
            variant="outline"
            onClick={runComplianceAudit}
            disabled={isRefreshing}
            data-testid="run-audit-cta"
          >
            {isRefreshing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Running Audit...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4 mr-2" />
                Run Compliance Audit
              </>
            )}
          </Button>
        )}
        </>
        )}
      </CardContent>
    </Card>
  );
}

// Error boundary wrapper to prevent widget crashes from blanking the entire page
class RiskWidgetErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('PursuitRiskWidget error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="p-6 text-center text-sm text-slate-500">
            Risk assessment temporarily unavailable. Try refreshing the page.
          </CardContent>
        </Card>
      );
    }
    return this.props.children;
  }
}

export function PursuitRiskWidgetSafe(props) {
  return (
    <RiskWidgetErrorBoundary>
      <PursuitRiskWidget {...props} />
    </RiskWidgetErrorBoundary>
  );
}

export default PursuitRiskWidgetSafe;
